'use client'

import { Banner } from '@qr-front/ui/components/Banner'
import { differenceInCalendarDays } from 'date-fns'
import { useAtom, useAtomValue } from 'jotai'
import { useLocale, useTranslations } from 'next-intl'
import { useEffect, useMemo, useRef } from 'react'
import { User } from 'types/auth'
import { Subscription, UserStatusEnum } from 'types/subscription'

import { userAtom } from '@/global-state'
import { useRouter } from '@/i18n-routing'
import { TRouter } from '@/i18n-routing'
import { TSvgIcon, TTranslationFunction } from '@/types'

import { getCurrentSubscriptionFromPms } from '../Subscription/getCurrentSubscription'
import {
	subscriptionAtom,
	subscriptionLoadingAtom,
} from '../Subscription/subscriptionState'
import { BannerDialog } from './BannerDialog'
import {
	bannerConfig as defaultBannerConfig,
	deactivatedBannerConfig,
} from './config'

export type TBannerConfig = {
	type: 'info' | 'error'
	Icon: TSvgIcon
	title: string
	description: string
	actionLabel: string
	onAction: () => void
}

export type TBannerConfigFunction = (
	router: TRouter,
	subscription: Subscription,
	t: TTranslationFunction,
	daysRemaining?: number,
) => Partial<Record<UserStatusEnum, TBannerConfig>>

interface BannersProps {
	bannerConfig?: TBannerConfigFunction
	className?: string
}

export const Banners = ({
	bannerConfig = defaultBannerConfig,
	className = 'mb-4',
}: BannersProps = {}) => {
	const [subscription, setSubscription] = useAtom(subscriptionAtom)
	const [isLoadingSubscription, setIsLoadingSubscription] = useAtom(
		subscriptionLoadingAtom,
	)
	const router = useRouter()
	const user = useAtomValue(userAtom)
	const locale = useLocale()

	const t = useTranslations()
	const hasFetchedRef = useRef(false)
	const previousUserStatusRef = useRef<UserStatusEnum | undefined>(
		user?.status,
	)

	useEffect(() => {
		if (user?.status !== previousUserStatusRef.current) {
			previousUserStatusRef.current = user?.status
			hasFetchedRef.current = false
		}

		if (
			!subscription &&
			!isLoadingSubscription &&
			user &&
			!hasFetchedRef.current
		) {
			hasFetchedRef.current = true
			setIsLoadingSubscription(true)
			getCurrentSubscriptionFromPms(locale)
				.then((subscription) => {
					setSubscription(subscription)
				})
				.catch((error) => {
					console.warn('Failed to fetch subscription:', error)
					// Don't reset hasFetchedRef to prevent infinite retry loops
					// The subscription fetch will be retried when user navigates or page refreshes
				})
				.finally(() => {
					setIsLoadingSubscription(false)
				})
		}
	}, [
		subscription,
		isLoadingSubscription,
		user,
		locale,
		setSubscription,
		setIsLoadingSubscription,
	])

	const userStatus: UserStatusEnum | undefined = (user as User)?.deactivatedAt
		? UserStatusEnum.DEACTIVATED
		: user?.status

	const config = useMemo(() => {
		if (!userStatus) return null

		if (userStatus === UserStatusEnum.DEACTIVATED) {
			return deactivatedBannerConfig(router, t)
		}

		if (!subscription) return null

		let daysRemaining = 0
		if (subscription.expiresAt) {
			const expiresAtDate = new Date(subscription.expiresAt)
			const differenceDate = differenceInCalendarDays(
				expiresAtDate,
				new Date(),
			)
			daysRemaining = Math.max(0, differenceDate)
		}
		return bannerConfig(router, subscription, t, daysRemaining)[userStatus]
	}, [router, userStatus, subscription, t, bannerConfig])

	if (!config || !userStatus) {
		return null
	}

	return (
		<div className={className}>
			<Banner
				type={config.type}
				Icon={config.Icon}
				title={config.title}
				description={config.description}
				actionLabel={config.actionLabel}
				onAction={config.onAction}
			/>
			<BannerDialog />
		</div>
	)
}
